import { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { ResponseError } from "@nens/rana/api-v1";
import { TenantContext } from "@nens/ranalib";
import { useCompareEffect } from "@nens/react/hooks";
import { ApiError } from "@nens/react-query-utils";
import { Raster, RasterTypeEnum, RevisionRaster } from "@nens/threedi/openapi-client";

import { VisibleLayer } from "@/components/layer-control";
import { FILE_NAME_KEY } from "@/config/query-parameter-consts";
import { RDC_RASTER_STYLE_BY_TYPE } from "@/config/rdc-raster-style-constants";
import { useDeleteProjectFileOrDirectory } from "@/features/project-file";
import { RanaCogRasterLayer } from "@/lib/rana-map/components/rana-map-layers";
import { useCogColorRangeQueries } from "@/queries/query-cog-color-range";
import { useFileDescriptorViewQuery } from "@/queries/query-file-descriptors";
import { useThreediModelQuery } from "@/queries/query-hcc-models";
import { useHccRasterListQuery } from "@/queries/query-hcc-raster";
import { useHccRasterDownloadQueries } from "@/queries/query-hcc-raster-download";
import { useGetStyleByFeature } from "@/queries/query-style-by-features";
import { useVectoryStyleQuery } from "@/queries/query-style-sprite";
import type { ProjectFile } from "@/types/api-model-simple-names";
import { SchematisationFileDescriptor } from "@/types/descriptor-types";
import { getBoundsFromSchematisation } from "@/utils/get-bounds-from-schematisation";
import {
  getVectorDescriptorLayers,
  getVectorDescriptorSprite,
} from "@/utils/get-vector-descriptor-layers-and-sprite";
import { remapStopsToRange } from "@/utils/raster-color-stops";

// FUTURE: This hook now combines query orchestration, raster color-mode state, and layer shaping.
// When we add the same behavior to publications, split this into smaller pure helpers and
// dedicated hooks so preview/publication can share logic without duplicating side-effects.
// This function takes an descriptor id and gets, sets and returns all the necessary information to visualize said descriptor.

type UseSchematisationPreviewProps = {
  file: ProjectFile;
  descriptor: SchematisationFileDescriptor;
};

export const EXTENT_LAYER_ID = "extent-layer";

const COG_RASTER_LAYER_PREFIX = "cog-raster-layer";

type RasterColorMode =
  | { type: "bounds"; bounds: [number, number, number, number] }
  | { type: "range"; range: [number, number] }
  | undefined;

function getRasterLayerId(rasterId: number) {
  return `${COG_RASTER_LAYER_PREFIX}-${rasterId}`;
}

function getRasterDisplayName(name: string) {
  return name.replace(/\.[^/.]+$/, "").replace(/_/g, " "); // "raster_dem.tif" -> "raster dem"
}

// Only keep rasters we actually render (DEM_RAW is not shown in preview).
// This also matches up with the RevisionRaster type we use to fetch the download URL.
// Reverse the order and then put DEM first to match the order in the desktop client
function getPreviewRasters(rasters: Raster[]): RevisionRaster[] {
  return rasters
    .filter(
      (raster): raster is RevisionRaster =>
        raster.type !== RasterTypeEnum.DEM_RAW_FILE && raster.file?.state === "uploaded"
    )
    .reverse()
    .sort((a, b) => {
      const aIsDem = a.type === RasterTypeEnum.DEM_FILE;
      const bIsDem = b.type === RasterTypeEnum.DEM_FILE;

      if (aIsDem === bIsDem) return 0;
      return aIsDem ? -1 : 1;
    });
}

function resolveRasterColorRange({
  colorMode,
  boundsColorRange,
  defaultColorRange,
}: {
  colorMode: RasterColorMode;
  boundsColorRange: [number, number] | undefined;
  defaultColorRange: [number, number] | undefined;
}) {
  if (colorMode?.type === "range") return colorMode.range;
  if (colorMode?.type === "bounds") return boundsColorRange ?? defaultColorRange;

  return defaultColorRange;
}

export const useSchematisationPreview = ({ file, descriptor }: UseSchematisationPreviewProps) => {
  const { tenant } = useContext(TenantContext);
  // Layer visibility state used by layer control and map rendering.
  const [visibleLayers, setVisibleLayers] = useState<VisibleLayer[]>([]);
  const [rasterColorModes, setRasterColorModes] = useState<Record<string, RasterColorMode>>({});

  // Navigation setup used after deleting the file reference.
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const onSuccess = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(FILE_NAME_KEY);
    navigate({ search: newSearchParams.toString() });
  };

  // Deletion action for invalid/deleted schematisation references.
  const { isMutating, mutate } = useDeleteProjectFileOrDirectory([file]);
  const deleteFile = () => mutate(undefined, { onSuccess });

  // Fetch model and associated rasters, then resolve downloadable COG URLs.

  const { data: model, isLoading: isLoadingRevision } = useThreediModelQuery({
    schematisationId: descriptor.meta.id,
    revisionId: descriptor.meta.revisionId ?? undefined,
  });
  const { data: rasterFileList, isLoading: isLoadingFileList } = useHccRasterListQuery(model);
  const previewRasters = useMemo(
    () => getPreviewRasters(rasterFileList?.results ?? []),
    [rasterFileList?.results]
  );

  const rasterDownloadQueries = useHccRasterDownloadQueries(
    previewRasters.map((raster) => ({
      modelId: model?.id,
      raster,
    }))
  );

  // Resolve vector style information and build map vector layers.

  const descriptorId = descriptor.id;
  const vectorMeta = descriptor.meta.vector;
  const featureTypeDefinition = vectorMeta?.featureTypeDefinition;
  const { data: featureTypeStyleDefinitions, isLoading: isLoadingStyleByFeatures } =
    useGetStyleByFeature();
  const styleId = featureTypeDefinition
    ? featureTypeStyleDefinitions?.[featureTypeDefinition].style_id
    : undefined;
  const { data: styleJson, isLoading: isLoadingStyleJson } = useVectoryStyleQuery(styleId);

  const { data: descriptorView, isLoading: isLoadingLayers } = useFileDescriptorViewQuery(
    descriptor.id,
    "pmtiles"
  );
  const vectorLayers = useMemo(
    () =>
      vectorMeta && descriptorView && !isLoadingStyleByFeatures && !isLoadingStyleJson
        ? getVectorDescriptorLayers({
            descriptorId,
            meta: vectorMeta,
            styleJson,
            descriptorView,
            styleId: styleId ?? null,
          })
        : [],
    [
      vectorMeta,
      descriptorView,
      isLoadingStyleByFeatures,
      isLoadingStyleJson,
      descriptorId,
      styleJson,
      styleId,
    ]
  );

  const sprite =
    !isLoadingStyleByFeatures && !isLoadingStyleJson && descriptorId && styleJson
      ? getVectorDescriptorSprite({ descriptorId, styleId: styleId ?? null, tenant, styleJson })
      : null;

  const sprites = sprite ? [sprite] : [];

  // Check if schematisation still exists and derive bounds for map display.

  const {
    data: schematisation,
    isError,
    error,
    isLoading: isFetchingSchematisationExistence,
  } = useFileDescriptorViewQuery(descriptor.id, "threedi-schematisation");

  const schematisationHasBeenDeleted =
    isError &&
    ((error instanceof ResponseError && error.response.status === 404) ||
      (error instanceof ApiError && error.getStatusCode() === 404));

  const bounds = useMemo(() => {
    if (schematisation) return getBoundsFromSchematisation(schematisation) ?? undefined;

    return descriptor.meta.vector?.envelope as [number, number, number, number] | undefined; // FUTURE - remove as when backend typing is more strict
  }, [descriptor.meta.vector?.envelope, schematisation]);

  // Build a stable raster-id -> download-url map so filtering does not break URL assignment.
  const rasterDownloadUrlById = useMemo(() => {
    const rasterResults = previewRasters;
    return new Map(
      rasterResults.map((raster, index) => [raster.id, rasterDownloadQueries[index]?.data] as const)
    );
  }, [previewRasters, rasterDownloadQueries]);

  // Build raster layer metadata and attach the matching download URL.
  const rastersWithUrl = previewRasters.map((raster) => ({
    ...raster,
    url: rasterDownloadUrlById.get(raster.id),
  }));

  const defaultColorRangeQueries = useCogColorRangeQueries(
    rastersWithUrl.map((raster) => ({
      rasterId: raster.id ?? 0,
      rasterUrl: raster.url,
      bounds: bounds,
      rasterType: raster.type,
    }))
  );

  const boundsColorRangeQueries = useCogColorRangeQueries(
    rastersWithUrl.map((raster) => {
      const rasterId = raster.id ?? 0;
      const controlLayerId = getRasterLayerId(rasterId);
      const mode = rasterColorModes[controlLayerId];

      // If the color mode is not set to "bounds", we can just use the default, so we return undefined for the rasterUrl and bounds. This will cause the query to skip fetching the color range for this raster.
      if (mode?.type !== "bounds")
        return {
          rasterId,
          rasterUrl: undefined,
          bounds: undefined,
          rasterType: undefined,
        };

      return {
        rasterId,
        rasterUrl: raster.url,
        bounds: mode.bounds,
        rasterType: raster.type,
      };
    })
  );

  const setRasterColorRange = useCallback((layerId: string, range: [number, number]) => {
    setRasterColorModes((previous) => ({
      ...previous,
      [layerId]: { type: "range", range },
    }));
  }, []);

  const setRasterColorBounds = useCallback(
    (layerId: string, rasterBounds: [number, number, number, number]) => {
      setRasterColorModes((previous) => ({
        ...previous,
        [layerId]: { type: "bounds", bounds: rasterBounds },
      }));
    },
    []
  );

  const setRasterDefaultColor = useCallback((layerId: string) => {
    setRasterColorModes((previous) => ({
      ...previous,
      [layerId]: undefined,
    }));
  }, []);

  const rasterLayers: RanaCogRasterLayer[] = rastersWithUrl.flatMap<RanaCogRasterLayer>(
    (raster, index) => {
      const type = raster.type;
      const isDem = type === RasterTypeEnum.DEM_FILE;
      const rasterId = raster.id ?? 0;
      const layerId = getRasterLayerId(rasterId);
      const baseName = getRasterDisplayName(raster.name);
      const defaultColorRange = defaultColorRangeQueries[index]?.data;
      const rdcStyle = type ? RDC_RASTER_STYLE_BY_TYPE[type] : undefined;
      const styleColorRange = rdcStyle?.range;
      const styleColorStops = rdcStyle?.stops;
      const colorMode = rasterColorModes[layerId];
      const colorRange = resolveRasterColorRange({
        colorMode,
        boundsColorRange: boundsColorRangeQueries[index]?.data,
        defaultColorRange,
      });

      if (!raster.url || !colorRange || !styleColorRange || !styleColorStops) {
        return [];
      }

      const colorStops = remapStopsToRange({
        stops: styleColorStops,
        sourceRange: styleColorRange,
        targetRange: colorRange,
      });

      if (isDem) {
        // Keep hillshade first so it appears as the top-most raster after render-order reversal.
        return [
          {
            id: `${layerId}-hillshade`,
            isHillshade: true,
            name: "hillshade",
            url: raster.url,
            colorRange: defaultColorRange,
            defaultColorRange: defaultColorRange,
            colorStops,
          },
          {
            id: layerId,
            isHillshade: false,
            name: baseName,
            url: raster.url,
            colorRange,
            defaultColorRange,
            colorStops,
          },
        ];
      }

      return [
        {
          id: layerId,
          isHillshade: false,
          name: baseName,
          url: raster.url,
          colorRange,
          defaultColorRange,
          colorStops,
        },
      ];
    }
  );

  // Merge vector and raster layers for layer control.
  const layers = [
    ...vectorLayers.map(({ layer }) => layer),
    ...rasterLayers.map(({ name }) => name),
  ];

  // If no layers add the extent layer
  if (!layers.length) {
    layers.push(EXTENT_LAYER_ID);
  }

  // Aggregate loading state from all async dependencies.
  const isLoadingFileDownload = rasterDownloadQueries.some((query) => query.isLoading);
  const isLoadingColorRanges = defaultColorRangeQueries.some((query) => query.isLoading);

  const isLoading =
    isLoadingLayers ||
    isLoadingFileList ||
    isLoadingFileDownload ||
    isLoadingColorRanges ||
    isLoadingRevision ||
    isLoadingStyleByFeatures ||
    isLoadingStyleJson ||
    isFetchingSchematisationExistence;

  useCompareEffect(() => {
    setVisibleLayers(layers.map((layer) => ({ descriptorId: descriptor.id, layer })));
  }, [layers]);

  return {
    schematisationHasBeenDeleted,
    isLoading,
    isFetchingSchematisationExistence,
    vectorLayers,
    visibleLayers,
    setVisibleLayers,
    bounds,
    isMutating,
    deleteFile,
    rasterLayers,
    setRasterColorRange,
    setRasterColorBounds,
    setRasterDefaultColor,
    layers,
    sprites,
  };
};
